
import { Manifest, PublicationType, BlockType, ConnectorDetails, MediaRule, ManifestVariant, Block } from "../types";

/**
 * Returns the list of block types allowed for a specific publication type
 * based on the variants defined in the manifest.
 * If no variants are defined, falls back to supports.blocks.
 */
export const getAllowedBlocksForType = (manifest: Manifest, type: PublicationType): BlockType[] => {
    const typeRules = manifest.rules.publicationTypes[type];
    
    // If no specific rules/variants for this type, fallback to general support
    if (!typeRules || !typeRules.variants || typeRules.variants.length === 0) {
        return manifest.supports.blocks;
    }

    // Collect all unique allowedBlocks from all variants for this publication type
    const allowed = new Set<BlockType>();
    typeRules.variants.forEach(v => {
        v.allowedBlocks.forEach(b => allowed.add(b));
    });

    return Array.from(allowed);
};

/**
 * Helper to get the effective max items for a media type based on publication type variants.
 * It looks for the "loosest" constraint among allowed variants (e.g., if one variant allows 1 and another 10, it returns 10).
 */
export const getEffectiveMediaLimit = (manifest: Manifest, pubType: PublicationType, mediaType: 'photo' | 'video' | 'file'): number => {
    const typeRules = manifest.rules.publicationTypes[pubType];
    const globalRule = manifest.rules[mediaType];
    const globalMax = globalRule?.maxItems ?? Infinity;

    // If no specific variants for this type, return global limit
    if (!typeRules || !typeRules.variants || typeRules.variants.length === 0) {
        return globalMax;
    }

    // Check variants constraints
    // We want the MAXIMUM of the limits defined in variants (e.g. Single vs Carousel -> we want Carousel limit)
    // If a variant doesn't specify a constraint, it implies the global limit applies for that variant.
    const variantLimits = typeRules.variants.map(v => {
        // Check for specific media type constraint (e.g., 'photo') or generic 'media' constraint
        const specificConstraint = v.constraints?.[mediaType]?.maxItems;
        const genericConstraint = v.constraints?.media?.maxItems; // 'media' usually covers both photo/video
        
        if (specificConstraint !== undefined) return specificConstraint;
        if (genericConstraint !== undefined) return genericConstraint;
        
        return globalMax;
    });

    if (variantLimits.length === 0) return globalMax;

    return Math.max(...variantLimits);
};

/**
 * Calculates a "Permissive" Universal Manifest.
 * It ensures safety by taking strict intersections of rules where necessary (like max file size),
 * but unions of capabilities (like supported blocks).
 */
export const mergeManifests = (manifests: Manifest[], type: PublicationType): Manifest => {
    if (manifests.length === 0) {
        return createEmptyManifest();
    }

    // 1. Blocks: Union (Allow if supported by at least one)
    const allSupportedBlocks = Array.from(new Set(
        manifests.flatMap(m => getAllowedBlocksForType(m, type))
    ));

    // 2. Text Formatting: Union
    const allSupportedFormatting = Array.from(new Set(
        manifests.flatMap(m => m.supports.textFormatting || [])
    ));

    // 3. Helper for Media Rules (intersection of constraints)
    // We want the stricter limit for safety (min), but intersection for allowed types.
    const mergeMediaRule = (mediaType: 'photo' | 'video' | 'file'): MediaRule | undefined => {
        // Filter manifests that actually support this media type in their rules
        const relevantManifests = manifests.filter(m => m.rules[mediaType]);
        
        if (relevantManifests.length === 0) return undefined;

        // Calculate Strictest Limits
        // CRITICAL UPDATE: Use getEffectiveMediaLimit to respect publication type constraints (e.g. Story limit vs Feed limit)
        const maxItems = Math.min(...relevantManifests.map(m => getEffectiveMediaLimit(m, type, mediaType)));
        
        const maxSizeMb = Math.min(...relevantManifests.map(m => m.rules[mediaType]?.maxSizeMb ?? Infinity));
        
        // Allowed Mime Types: INTERSECTION (Only allow types supported by ALL selected networks that support this media category)
        const mimeArrays = relevantManifests
            .map(m => m.rules[mediaType]?.allowedMimeTypes)
            .filter((arr): arr is string[] => !!arr);

        let commonMimes: string[] | undefined = undefined;
        if (mimeArrays.length > 0) {
            commonMimes = mimeArrays.reduce((acc, curr) => acc.filter(mime => curr.includes(mime)));
        }

        // Mixed Types logic: 
        // We use 'some' instead of 'every'. If at least one network supports mixing, we allow it in the editor.
        // Incompatible networks will be caught by the per-network validation loop in UniversalEditor.
        const allowMixed = relevantManifests.some(m => m.rules[mediaType]?.allowMixedTypesInGroup !== false);

        return {
            maxItems: maxItems === Infinity ? 10 : maxItems,
            maxSizeMb: maxSizeMb === Infinity ? 50 : maxSizeMb,
            allowedMimeTypes: commonMimes,
            allowMixedTypesInGroup: allowMixed
        };
    };

    const textMaxLength = Math.min(...manifests.map(m => m.rules.text?.maxLength ?? Infinity));
    const pollMaxOptions = Math.min(...manifests.map(m => m.rules.poll?.maxOptions ?? Infinity));

    // 4. Calculate Supported Media Types for the active publication type (Intersection)
    // If a manifest doesn't specify supportedMediaTypes, we assume it supports all (['photo', 'video']) for backward compatibility
    const allMediaSets = manifests.map(m => 
        m.rules.publicationTypes[type]?.supportedMediaTypes || (['photo', 'video'] as ('photo' | 'video')[])
    );

    // Intersect: The result must be supported by ALL selected networks
    const allowedMediaTypes = allMediaSets.reduce((acc, curr) => 
        acc.filter(t => curr.includes(t)), 
        ['photo', 'video'] as ('photo' | 'video')[]
    );

    return {
        supports: {
            publicationTypes: [type],
            blocks: allSupportedBlocks,
            textFormatting: allSupportedFormatting
        },
        rules: {
            text: { maxLength: textMaxLength === Infinity ? 4096 : textMaxLength },
            photo: mergeMediaRule('photo'),
            video: mergeMediaRule('video'),
            file: mergeMediaRule('file'),
            poll: { maxOptions: pollMaxOptions === Infinity ? 10 : pollMaxOptions },
            publicationTypes: {
                [type]: {
                    variants: [] as ManifestVariant[], // We don't merge variants, validation happens per-connector
                    supportedMediaTypes: allowedMediaTypes
                }
            }
        },
        modifiers: [] 
    };
};

export const createEmptyManifest = (): Manifest => ({
    supports: { publicationTypes: [], blocks: [] },
    rules: { publicationTypes: {} }
});

/**
 * Checks which of the provided connectors DO NOT support a specific block type.
 */
export const getUnsupportedConnectors = (
    connectors: ConnectorDetails[], 
    blockType: BlockType
): ConnectorDetails[] => {
    return connectors.filter(c => !c.manifest.supports.blocks.includes(blockType));
};

/**
 * Analyzes the current blocks against the manifest and determines:
 * 1. Which blocks are valid (compliant).
 * 2. Which blocks are ignored (non-compliant).
 * 3. The specific reason/combination rule used.
 */
export interface ComplianceResult {
    validBlocks: Block[];
    ignoredBlocks: Block[];
    reason?: string;
    warnings?: string[];
    variantName?: string;
}

export const getCompliantBlocks = (
    allBlocks: Block[],
    manifest: Manifest,
    type: PublicationType,
    connectorId?: string // Optional check for disabledNetworkIds
): ComplianceResult => {
    // 1. Get supported block types for this publication type in this network
    const supportedTypes = getAllowedBlocksForType(manifest, type);
    
    // Check specific media types supported by this pubType (e.g., Feed supports only 'photo', but Story supports 'photo' & 'video')
    const supportedMediaTypes = manifest.rules.publicationTypes[type]?.supportedMediaTypes; // e.g. ['photo']

    // 2. Separate blocks into Candidates (Type Supported) and Unsupported (Type Invalid)
    const candidates: Block[] = [];
    const unsupportedByType: Block[] = [];
    const internalWarnings: string[] = [];

    allBlocks.forEach(b => {
        // Skip if explicitly disabled by user for this connector
        if (connectorId && b.disabledNetworkIds?.includes(connectorId)) return;

        if (supportedTypes.includes(b.type)) {
            // SPECIAL CHECK: Filter Media content based on supportedMediaTypes
            if ((b.type === 'media' || b.type === 'file') && b.content?.files && supportedMediaTypes) {
                // Determine which files are valid
                const validFiles = (b.content.files as any[]).filter(f => {
                    const fileType = (f.type?.startsWith('video') || f.type === 'video') ? 'video' : 'photo';
                    return supportedMediaTypes.includes(fileType);
                });

                // If some files were dropped, we create a modified block and log a warning
                if (validFiles.length < b.content.files.length) {
                    const droppedCount = b.content.files.length - validFiles.length;
                    const allowedStr = supportedMediaTypes.map(t => t === 'photo' ? 'фото' : 'видео').join(', ');
                    internalWarnings.push(`Исключено ${droppedCount} файл(ов): тип не поддерживается в этом режиме (разрешено: ${allowedStr}).`);
                    
                    // Push a CLONE of the block with filtered files
                    candidates.push({
                        ...b,
                        content: { ...b.content, files: validFiles }
                    });
                } else {
                    candidates.push(b);
                }
            } else {
                candidates.push(b);
            }
        } else {
            unsupportedByType.push(b);
        }
    });

    const typeRules = manifest.rules.publicationTypes[type];

    // If no variants defined, strict combination rules don't apply beyond basic type support
    if (!typeRules || !typeRules.variants || typeRules.variants.length === 0) {
        // Even if no variants, we must warn about blocks that are unsupported by type
        if (unsupportedByType.length > 0) {
            const invalidNames = Array.from(new Set(unsupportedByType.map(b => getBlockTypeName(b.type))));
            return {
                validBlocks: candidates,
                ignoredBlocks: unsupportedByType,
                variantName: 'Default',
                reason: `Эти блоки не поддерживаются: ${invalidNames.join(', ')}.`,
                warnings: internalWarnings
            };
        }
        return { validBlocks: candidates, ignoredBlocks: [], variantName: 'Default', warnings: internalWarnings };
    }

    // 3. Find the "Best Match" variant among candidates
    // Strategy: Find the variant that allows the maximum number of our candidate blocks.
    
    let bestVariant: ManifestVariant | null = null;
    let bestScore = -1;
    let bestValidSubset: Block[] = [];

    for (const variant of typeRules.variants) {
        // Only consider blocks that this variant allows
        const validSubset = candidates.filter(b => variant.allowedBlocks.includes(b.type));
        
        // Check Required Blocks
        if (variant.requiredBlocks) {
            const hasRequired = variant.requiredBlocks.every(req => validSubset.some(b => b.type === req));
            if (!hasRequired) continue; // This variant is not applicable because we miss required blocks
        }

        // Score calculation
        let score = validSubset.length * 10;
        // Boost score for prioritized content to break ties
        if (validSubset.some(b => b.type === 'text')) score += 4;
        if (validSubset.some(b => b.type === 'media')) score += 3;
        if (validSubset.some(b => b.type === 'poll')) score += 2;

        if (score > bestScore) {
            bestScore = score;
            bestVariant = variant;
            bestValidSubset = validSubset;
        }
    }

    // Combine unsupported-by-type and unsupported-by-variant
    const ignoredFromVariants = bestVariant 
        ? candidates.filter(b => !bestValidSubset.includes(b)) 
        : candidates; // If no variant matched (shouldn't happen often if manifest is good), all candidates ignored

    const allIgnored = [...unsupportedByType, ...ignoredFromVariants];

    if (!bestVariant && candidates.length > 0) {
         return { 
            validBlocks: [], 
            ignoredBlocks: allIgnored, 
            reason: "Нет подходящей комбинации блоков для этой сети.",
            warnings: internalWarnings 
        };
    }

    // Construct reason if there are ignored blocks
    let reason;
    if (allIgnored.length > 0) {
        const invalidTypeNames = Array.from(new Set(unsupportedByType.map(b => getBlockTypeName(b.type))));
        const variantIgnoredNames = Array.from(new Set(ignoredFromVariants.map(b => getBlockTypeName(b.type))));

        const parts = [];
        if (invalidTypeNames.length > 0) {
            parts.push(`Не поддерживается: ${invalidTypeNames.join(', ')}. Блок будет проигнорирован.`);
        }
        if (bestVariant && variantIgnoredNames.length > 0) {
            parts.push(`Схема "${bestVariant.label}" исключает: ${variantIgnoredNames.join(', ')}`);
        }
        
        reason = parts.join('. ') + '.';
    }

    return {
        validBlocks: bestVariant ? bestValidSubset : candidates,
        ignoredBlocks: allIgnored,
        variantName: bestVariant?.label || 'Default',
        reason,
        warnings: internalWarnings
    };
};

export const getBlockTypeName = (type: BlockType): string => {
    switch(type) {
        case 'text': return 'Текст';
        case 'media': return 'Медиа';
        case 'file': return 'Файл';
        case 'poll': return 'Опрос';
        default: return type;
    }
};

/**
 * Validates if a set of blocks is allowed by the manifest variants.
 * Deprecatedish: Use getCompliantBlocks for detailed info.
 */
export const validateBlockCombination = (
    currentBlockTypes: BlockType[], 
    manifest: Manifest, 
    type: PublicationType
): boolean => {
    const typeRules = manifest.rules.publicationTypes[type];
    
    if (!typeRules || !typeRules.variants || typeRules.variants.length === 0) {
        return currentBlockTypes.every(b => manifest.supports.blocks.includes(b));
    }

    return typeRules.variants.some(variant => {
        const allAllowed = currentBlockTypes.every(b => variant.allowedBlocks.includes(b));
        if (!allAllowed) return false;
        if (variant.requiredBlocks) {
            const allRequiredPresent = variant.requiredBlocks.every(req => currentBlockTypes.includes(req));
            if (!allRequiredPresent) return false;
        }
        return true;
    });
};
